import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { MM_PER_INCH, MM_PER_METRE } from 'utils/unitConverter/conversionFactors'
import type { TUnitConverterOptionsWithUnits, TUnitConverterTypicalReturn } from 'utils/unitConverter/types'

type TEmitterSpacingUnit = 'in' | 'mm' | 'm'

const DEFAULT_DECIMAL_PLACES = 1
const MM_IN: Record<TEmitterSpacingUnit, number> = { in: MM_PER_INCH, mm: 1, m: MM_PER_METRE }

export const emitterSpacing: Record<
  'emitterSpacing',
  (
    spacing?: number | null,
    options?: TUnitConverterOptionsWithUnits<TEmitterSpacingUnit>,
  ) => TUnitConverterTypicalReturn
> = {
  emitterSpacing: (
    spacing: number | null = null,
    {
      decimalPlaces = DEFAULT_DECIMAL_PLACES,
      inputUnit = 'mm',
      outputUnit = userDetailsStore.getState().rain === 'IMPERIAL' ? 'in' : 'mm',
    }: TUnitConverterOptionsWithUnits<TEmitterSpacingUnit> = {},
  ) => {
    const convertedValue = isNil(spacing) ? null : (spacing * MM_IN[inputUnit]) / MM_IN[outputUnit]

    const measurement = {
      in: translate.measurements.inches,
      mm: translate.measurements.millimeters,
      m: translate.measurements.meters,
    }[outputUnit]

    const unitSymbol = measurement.unit()
    const label = translate.phrases.banyanApp('Emitter spacing')

    return {
      categoryTitle: () => translate.phrases.templates('{{label}} ({{unitSymbol}})', { label, unitSymbol }),
      categoryTitleWithoutUnit: () => label,
      defaultNumberOfDecimalPlaces: () => DEFAULT_DECIMAL_PLACES,
      suffix: () => unitSymbol,
      shortTitle: () => label,
      title: () => translate.phrases.templates('{{label}} ({{unitSymbol}})', { label, unitSymbol }),
      titleWithoutUnit: () => label,
      value: () => (isNil(convertedValue) ? null : +convertedValue.toFixed(decimalPlaces)),
      valueAsString: () => measurement.value(convertedValue, decimalPlaces),
      valueWithSuffix: () => measurement.valueWithUnit(convertedValue, decimalPlaces),
      valueWithNoRounding: () => convertedValue,
    }
  },
}
